import { useLayoutEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Button, Result } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { api } from '@/shared/api';
import { ApiRoutes, PageRoutes } from '@/shared/router';
import { themeConfig, useThemeContext } from '@/shared/theme';
import { Col } from '@/shared/ui';
import { captureError } from '@/shared/utils/error-reporting';

const searchParams = new URLSearchParams(window.location.search);
const email = searchParams.get('email');

export const AuthorisationError = () => {
  const { theme } = useThemeContext();
  const navigate = useNavigate();
  const { error: authError, logout } = useAuth0();
  const { state } = useLocation();

  const [loading, setLoading] = useState(false);
  const [mailSent, setMailSent] = useState(false);

  const { error } = state || {};
  const { email: emailData } = error || {};

  const resendActivationLink = async () => {
    setLoading(true);
    try {
      await api.post(ApiRoutes.USERS_VERIFICATION_EMAIL, {
        email,
      });
      setMailSent(true);
    } catch (err) {
      captureError(err, {
        module: 'auth',
        action: 'resend_activation_email',
        metadata: { email },
      });
    }
    setLoading(false);
  };

  useLayoutEffect(() => {
    if (authError && logout && emailData) {
      captureError(authError, {
        module: 'auth',
        action: 'logout',
        metadata: { emailData },
      });

      logout({
        logoutParams: {
          returnTo: `${import.meta.env.VITE_BASE_URL}${
            PageRoutes.AUTHORISATION_ERROR
          }?email=${encodeURIComponent(emailData)}`,
        },
      });
    }
  }, [authError, logout, emailData]);

  if (authError) {
    return null;
  }

  return (
    <Wrapper>
      {!mailSent ? (
        <Result
          status="404"
          subTitle={
            <span style={{ color: themeConfig[theme].colors.text }}>
              You tried to log in but you have not activated your account. If
              you want to resend activation link, please click on the button
              below.
            </span>
          }
          extra={
            <Col style={{ gap: 16 }}>
              <Button
                type="primary"
                key="resend-email"
                loading={loading}
                onClick={resendActivationLink}
              >
                Resend activation email
              </Button>
              Or
              <Button onClick={() => navigate(PageRoutes.LOGIN)}>
                Go to login page
              </Button>
            </Col>
          }
          style={{ maxWidth: 500 }}
        />
      ) : (
        <Result
          status="success"
          subTitle={
            <span style={{ color: themeConfig[theme].colors.text }}>
              Verification email successfully sent. Check your mailbox.
            </span>
          }
          extra={[
            <Button
              type="default"
              key="login"
              onClick={() => navigate(PageRoutes.LOGIN)}
            >
              Go to login page
            </Button>,
          ]}
        />
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
`;
